import { Dropdown } from "primereact/dropdown";
import React, { useEffect, useRef, useState } from "react";
import { JVInput } from "../../../../shared/JVInput";
import { Button } from "primereact/button";
import { useForm, Controller, useWatch } from "react-hook-form";
import { IBranchMastDropdown, ICompanyMastDropdown } from "../../../../models/IDropdown";
import { Fetch } from "../../../../shared/Fetch";
import { DataTable } from "primereact/datatable";
import { Column, ColumnEditorOptions } from "primereact/column";
import { safeFormatDate } from "../../../../models/DateFormatMapper";
import { ILogin } from "../../../../models/Login";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

interface IProps {
  reload: boolean;
  setReload: (reload: boolean) => void;
  toast: any;
}

interface IMonsterBranchSetting {
  id?: number;
  branch_ID: number;
  branchName?: string;
  no_Of_Views: number;
  created_By?: number;
  created_At?: string;
  updated_By?: number;
  updated_At?: string;
}

const schema = yup.object({
  branch_ID: yup.number().typeError("Branch is required").min(1, "Branch is required").required("Branch is required"),
  company_ID: yup.number().typeError("Company is required").min(1, "Company is required").required("Company is required"),
  viewCount: yup.number().typeError("Must be a number").positive("Must be greater than 0").integer("Must be a whole number").required("Number of Views Allowed is required"),
});

const MonsterBranchSettingForm = (props: IProps) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [tableSaveLoading, setTableSaveLoading] = useState<boolean>(false);
  const [branchList, setBranchList] = useState<Array<IBranchMastDropdown>>();
  const [tableData, setTableData] = useState<Array<IMonsterBranchSetting>>([]);
  const [focusIndex, setFocusIndex] = useState<number>(-1);
  const [editedIds, setEditedIds] = useState<Set<number>>(new Set());
  const [companyList, setCompanyList] = useState<Array<ICompanyMastDropdown>>([]);
  const dt = useRef<any>(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<any>({
    resolver: yupResolver(schema) as any,
    defaultValues: { branch_ID: null, viewCount: "", company_ID: null },
    shouldFocusError: false,
  });

  const loginObj: ILogin = JSON.parse(sessionStorage.getItem("auth") + "");
  const DateFormat = loginObj.date_Format;

  // const loadBranches = async () => {
  //   const response = await Fetch("Dropdown/branches", "GET", null, null, null);
  //   if (response) {
  //     const data: Array<IBranchMastDropdown> = await response.json();
  //     data.unshift({ branch_ID: 0, branchName: "-- SELECT --", company_ID: 0 });
  //     setBranchList(data);
  //   }
  // };

  const loadTableData = async () => {
    setTableLoading(true);
    const response = await Fetch("Monster_Setting/getMonsterBranchSetting", "GET", null, null, props.toast);
    if (response) {
      const data: Array<IMonsterBranchSetting> = await response.json();
      setTableData(data);
    }
    setTableLoading(false);
  };

  const loadCompany = async () => {
    const response = await Fetch("Monster_Setting/getCompanyformonster", "GET", null, null, null);
    if (response) {
      const data: Array<ICompanyMastDropdown> = await response.json();
      data.unshift({ company_ID: 0, company_Name: "-- SELECT --" });
      setCompanyList(data);
    }
  };

  const selectedCompany = useWatch({ control, name: "company_ID" });

  useEffect(() => {
    if (!selectedCompany || selectedCompany === 0) {
      setBranchList([]);
      return;
    }
    const loadBranches = async () => {
      const response = await Fetch("Monster_Setting/getBranchforMonster", "GET", null, `company_id=${selectedCompany}`, null);
      if (response) {
        const data: Array<IBranchMastDropdown> = await response.json();
        data.unshift({ branch_ID: 0, branchName: "-- SELECT --", company_ID: 0 });
        setBranchList(data);
      }
    };
    loadBranches();
  }, [selectedCompany]);

  useEffect(() => {
    // loadBranches();
    loadTableData();
    loadCompany();
  }, [props.reload]);

  const onsubmit = async (formData: any) => {
    setLoading(true);
    const payload = {
      branch_ID: formData.branch_ID,
      no_Of_Views: Number(formData.viewCount),
    };
    const resp = await Fetch("Monster_Setting/addMonsterBranchSetting", "POST", payload, null, props.toast);
    if (resp) {
      props.toast.current.show({ severity: "success", detail: "Added Successfully", life: 3000 });
      reset({ branch_ID: null, viewCount: "", company_ID: null });
      props.setReload(!props.reload);
    }
    setLoading(false);
  };

  const onTableSave = async () => {
    if (editedIds.size === 0) {
      props.toast.current.show({ severity: "warn", detail: "No changes to save.", life: 3000 });
      return;
    }
    setTableSaveLoading(true);
    const payload = tableData
      .filter((el) => editedIds.has(el.id!))
      .map((el) => ({
        ID: el.id,
        No_Of_Views: el.no_Of_Views,
      }));
    const resp = await Fetch("Monster_Setting/updateMonsterBranchSetting", "PUT", payload, null, props.toast);
    if (resp) {
      props.toast.current.show({ severity: "success", detail: "Updated Successfully", life: 3000 });
      props.setReload(!props.reload);
      setFocusIndex(-1);
      setEditedIds(new Set());
    }
    setTableSaveLoading(false);
  };

  const isPositiveInteger = (val: any) => {
    const n = Number(val);
    return Number.isInteger(n) && n > 0;
  };

  const onCellEditComplete = (e: any) => {
    const { rowData, newValue, field, originalEvent: event } = e;
    if (field === "no_Of_Views") {
      if (isPositiveInteger(newValue)) {
        rowData[field] = newValue;
        setEditedIds((prev) => new Set(prev).add(rowData.id));
      } else {
        event.preventDefault();
      }
    } else {
      if (newValue?.toString().trim().length > 0) {
        rowData[field] = newValue;
        setEditedIds((prev) => new Set(prev).add(rowData.id));
      } else {
        event.preventDefault();
      }
    }
    setTableData((prev) =>
      prev.map((item) => {
        if (item.id === rowData.id) {
          return {
            ...item,
            no_Of_Views: isPositiveInteger(rowData.no_Of_Views) ? Number(rowData.no_Of_Views) : item.no_Of_Views,
          };
        }
        return item;
      }),
    );
  };

  const cellEditor = (options: ColumnEditorOptions) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (options.editorCallback) options.editorCallback(e.target.value);
    };
    return <input style={{ width: "100%", margin: 0, padding: "3px 5px", border: "2px solid green", cursor: "pointer" }} value={options.value} type="number" onChange={handleChange} />;
  };

  return (
    <>
      <form onSubmit={handleSubmit(onsubmit)}>
        <div className="field flex align-items-end gap-2">
          <div className="field col-12 md:col-3">
            <label className="block mb-2">
              Company <span style={{ color: "red" }}>*</span>
            </label>
            <Controller
              name="company_ID"
              control={control}
              render={({ field }) => <Dropdown {...field} options={companyList} optionValue="company_ID" optionLabel="company_Name" placeholder="Select Company" filter showClear className={`w-full ${errors.company_ID ? "p-invalid" : ""}`} onChange={(e) => field.onChange(e.value)} />}
            />
            {errors.company_ID && <small className="p-error">{errors.company_ID.message as string}</small>}
          </div>
          <div className="field col-12 md:col-3">
            <label className="block mb-2">
              Branch <span style={{ color: "red" }}>*</span>
            </label>
            <Controller
              name="branch_ID"
              control={control}
              render={({ field }) => <Dropdown {...field} options={branchList} optionValue="branch_ID" optionLabel="branchName" placeholder="Select Branch" filter showClear className={`w-full ${errors.branch_ID ? "p-invalid" : ""}`} onChange={(e) => field.onChange(e.value)} />}
            />
            {errors.branch_ID && <small className="p-error">{errors.branch_ID.message as string}</small>}
          </div>

          <JVInput required type="number" column={4} label="Number of Views Allowed" control={control} name="viewCount" />

          <div className="flex align-items-end field col-12 md:col-3">
            <Button loading={loading} disabled={loading} style={{ width: "100px", height: "37px" }} label="ADD" type="submit" />
          </div>
        </div>
      </form>

      <DataTable style={{ border: "1px solid rgba(105,105,105,0.17)", borderTopWidth: "0", marginTop: "1rem" }} size="small" ref={dt} value={tableData} emptyMessage="No Data found." loading={tableLoading} showGridlines stripedRows responsiveLayout="scroll">
        <Column field="branchName" header="Branch" />
        <Column
          field="no_Of_Views"
          header="No of Views Allowed"
          body={(rowData: IMonsterBranchSetting, options: any) => (
            <p className={options.rowIndex === focusIndex ? "glow-loop" : ""} style={{ color: options.rowIndex === focusIndex ? "red" : "black" }}>
              {rowData.no_Of_Views}
            </p>
          )}
          editor={(options) => cellEditor(options)}
          onCellEditComplete={onCellEditComplete}
        />
        <Column header="Created At" body={(rowData: any) => safeFormatDate(rowData.created_At, "dd/MM/yyyy", DateFormat, true)} />
        <Column field="created_By" header="Created By" />
        <Column field="updated_By" header="Updated By" />
      </DataTable>

      <div className="col-12 md:col-12 flex gap-4 justify-content-end mt-2">
        <Button loading={tableSaveLoading} disabled={tableSaveLoading || tableData.length === 0} style={{ width: "100px" }} label="SAVE" type="button" onClick={onTableSave} />
      </div>
    </>
  );
};

export default MonsterBranchSettingForm;
